import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { resetPassword } from '../lib/api';
import AuthLayout from './AuthLayout';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <AuthLayout
        title="Invalid reset link"
        subtitle="This password reset link is missing a token. Please request a new one."
      >
        <p className="auth-help">
          <Link to="/forgot-password">Request a new reset link</Link>
        </p>
      </AuthLayout>
    );
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmation) {
      setError('Passwords do not match.');
      return;
    }

    setStatus('submitting');
    const result = await resetPassword({
      token,
      password,
      password_confirmation: confirmation,
    });

    if (result.success) {
      setStatus('success');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } else {
      setStatus('error');
      setError(result.error ?? 'Unable to reset password. The link may have expired.');
    }
  }

  if (status === 'success') {
    return (
      <AuthLayout
        title="Password reset"
        subtitle="Your password has been updated. Redirecting you to sign in…"
      />
    );
  }

  return (
    <AuthLayout title="Choose a new password" subtitle="Enter a new password for your account.">
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <label className="auth-field">
          <span>New password</span>
          <input
            type="password"
            required
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={event => setPassword(event.target.value)}
            disabled={status === 'submitting'}
          />
        </label>

        <label className="auth-field">
          <span>Confirm password</span>
          <input
            type="password"
            required
            autoComplete="new-password"
            minLength={8}
            value={confirmation}
            onChange={event => setConfirmation(event.target.value)}
            disabled={status === 'submitting'}
          />
        </label>

        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="auth-submit" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Resetting…' : 'Reset password'}
        </button>
      </form>

      <p className="auth-help">
        <Link to="/login">Back to sign in</Link>
      </p>
    </AuthLayout>
  );
}
